// src/components/elements/calendar/vl-calendar.tsx

import React, { useState, useMemo } from "react";
import VlCalendarMonth from "./vl-calendar-month";
import { getToday, parseDate } from "../../../utils/datesUtils";
import { DAYS_SHORTCUTS, langTranslate } from "../../../utils/calculations";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { formatDate } from "../../../utils/formater";

type DayShortcut = (typeof DAYS_SHORTCUTS)[number];
type Language = "DE" | "EN";

interface VlCalendarProps {
  isSelected?: (date: string) => boolean;
  isDisabled?: (dateOrWeekday: string | number) => boolean;
  isOccupied?: (date: string) => boolean;
  isPartially?: (date: string) => boolean;
  customClasses?: Record<string, (date: string) => boolean>;
  showWeeksNumber?: boolean;
  singleMonth?: boolean;
  defaultDate?: string;
  selectedDate?: { start: string | null; end: string | null };
  changeDate?: "start" | "end";
  maxDate?: string | null;
  firstDayOfWeek?: DayShortcut;
  maxMonthStart?: number;
  maxMonthEnd?: number;
  availableplan?: boolean;
  lang?: Language;
  onInput?: (date: string) => void;
}

export const VlCalendar: React.FC<VlCalendarProps> = ({
  isSelected,
  isDisabled,
  isOccupied,
  isPartially,
  customClasses,
  showWeeksNumber = false,
  singleMonth = false,
  defaultDate,
  selectedDate,
  changeDate = "start",
  maxDate,
  firstDayOfWeek = "mo",
  maxMonthStart = 2,
  maxMonthEnd = 2,
  availableplan = false,
  lang = "DE",
  onInput,
}) => {
  // Initialize dates
  const initialDate = defaultDate ? parseDate(defaultDate) : getToday();
  const today = getToday();
  const openingtimes = useSelector(
    (state: RootState) => state.stations.openingtimes,
  );
  const startDate = useSelector(
    (state: RootState) => state.userSession.startDate,
  );
  const endDate = useSelector((state: RootState) => state.userSession.endDate);

  // State
  const [currentMonthMonth, setCurrentMonthMonth] = useState(
    initialDate.getMonth(),
  );
  const [currentMonthYear, setCurrentMonthYear] = useState(
    initialDate.getFullYear(),
  );
  const [current, setCurrent] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  const todayMonthMonth = today.getMonth();
  const todayMonthYear = today.getFullYear();

  // Computed values
  const nextMonthMonth = useMemo(
    () => (currentMonthMonth === 11 ? 0 : currentMonthMonth + 1),
    [currentMonthMonth],
  );

  const nextMonthYear = useMemo(
    () => (currentMonthMonth === 11 ? currentMonthYear + 1 : currentMonthYear),
    [currentMonthMonth, currentMonthYear],
  );

  // Methods
  const moveBack = () => {
    setCurrent((prev) => prev - 1);
    if (currentMonthMonth === 0) {
      setCurrentMonthMonth(11);
      setCurrentMonthYear((prev) => prev - 1);
    } else {
      setCurrentMonthMonth((prev) => prev - 1);
    }
  };

  const moveForward = () => {
    setCurrent((prev) => prev + 1);
    if (currentMonthMonth === 11) {
      setCurrentMonthMonth(0);
      setCurrentMonthYear((prev) => prev + 1);
    } else {
      setCurrentMonthMonth((prev) => prev + 1);
    }
  };

  // Arrow visibility logic
  const showBackArrow = availableplan
    ? current >= -(maxMonthStart - 1)
    : current >= -(maxMonthStart - 1) ||
      currentMonthMonth > todayMonthMonth ||
      currentMonthYear > todayMonthYear;

  const showForwardArrow = singleMonth
    ? current <= maxMonthEnd - 1
    : current <= maxMonthEnd - 2;

  const handleHoveringUpdate = (state: boolean, date: string) => {
    if (!availableplan) {
      setIsHovering(state);
    }
  };

  return (
    <div
      className={`vl-calendar ${
        availableplan
          ? "vl-calendar__availableplan"
          : `vl-calendar__${changeDate}`
      }`}
    >
      {/* Back Arrow */}
      <span
        className={`vl-calendar__arrow vl-calendar__arrow--back ${
          !showBackArrow ? "disabled" : ""
        }`}
        onClick={showBackArrow ? moveBack : undefined}
      />
      {/* Forward Arrow */}
      <span
        className={`vl-calendar__arrow vl-calendar__arrow--forward ${
          !showForwardArrow ? "disabled" : ""
        }`}
        onClick={showForwardArrow ? moveForward : undefined}
      />
      {/* Current Month */}
      <VlCalendarMonth
        className="vl-calendar__month"
        month={currentMonthMonth}
        year={currentMonthYear}
        isSelected={isSelected}
        isDisabled={isDisabled}
        isOccupied={isOccupied}
        isPartially={isPartially}
        customClasses={customClasses}
        showWeeksNumber={showWeeksNumber}
        firstDayOfWeek={firstDayOfWeek}
        selectedDate={selectedDate}
        isHovering={isHovering}
        maxDate={maxDate}
        lang={lang}
        onInput={onInput}
        onHoveringUpdate={handleHoveringUpdate}
      />
      {/* Next Month */}
      {!singleMonth && (
        <VlCalendarMonth
          className="vl-calendar__month"
          month={nextMonthMonth}
          year={nextMonthYear}
          isSelected={isSelected}
          isDisabled={isDisabled}
          isOccupied={isOccupied}
          isPartially={isPartially}
          customClasses={customClasses}
          showWeeksNumber={showWeeksNumber}
          firstDayOfWeek={firstDayOfWeek}
          selectedDate={selectedDate}
          isHovering={isHovering}
          maxDate={maxDate}
          lang={lang}
          onInput={onInput}
          onHoveringUpdate={handleHoveringUpdate}
        />
      )}
      <div className="orm-flex orm-calendar-select">
        <div className="">
          Zeitraum:
          {/* {!availableplan && (
          <div className="">
            {langTranslate("dayof", lang)}{" "}
            <span>
              {changeDate === "start"
                ? langTranslate("pickup", lang)
                : langTranslate("delivery", lang)}
            </span>
            :
          </div>
        )} */}
          <span>
            {" "}
            <span
              className={`orm-calendar-select_date ${changeDate === "start" ? "active" : ""}`}
              onClick={() => onInput!("changetostart")}
            >
              {formatDate(startDate!, "dd.mm.yyyy")}
            </span>{" "}
            -{" "}
            <span
              className={`orm-calendar-select_date ${changeDate === "end" ? "active" : ""}`}
              onClick={() => onInput!("changetoend")}
            >
              {formatDate(endDate!, "dd.mm.yyyy")}
            </span>
          </span>
        </div>
        <div className="">
          <button className="orm-btn" onClick={() => onInput!("submit")}>
            Auswählen
          </button>
        </div>
      </div>
    </div>
  );
};

export default VlCalendar;
